"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.initialize = exports.isValidFormulaSyntax = exports.isValidHyperFormulaVariable = void 0;
const hyperformula_1 = require("hyperformula");
const common_1 = require("@finley-technologies/common");
const adapter_1 = require("../NodeEngine/adapter");
const utils_1 = require("../utils");
const config_1 = require("./config");
const customFunctions_1 = require("./customFunctions");
let validationInstance = null;
const isValidHyperFormulaVariable = (variable) => {
    if (!validationInstance) {
        validationInstance = hyperformula_1.HyperFormula.buildFromArray([], config_1.baseConfig);
    }
    // hyperformula has some strict rules about names of the variables.
    // for example `creditScrore620` is not a valid variable name.
    // this check lets us catch such errors upfront.
    const isAddable = validationInstance.isItPossibleToAddNamedExpression(variable, '42');
    return isAddable;
};
exports.isValidHyperFormulaVariable = isValidHyperFormulaVariable;
// this function should be used only for supplemental
// validation of formula syntax. It does not validate
// variables functions and such.
const isValidFormulaSyntax = (formula) => {
    if (!validationInstance) {
        validationInstance = hyperformula_1.HyperFormula.buildFromArray([], config_1.baseConfig);
    }
    // like rest of formula engine, we prepend formula with `=` as required
    // by hyperformula (if its not already there)
    const formulaWithEqualSign = formula.startsWith('=')
        ? formula
        : `=${formula}`;
    return validationInstance.validateFormula(formulaWithEqualSign);
};
exports.isValidFormulaSyntax = isValidFormulaSyntax;
// must register custom functions before creating a hyperformula instance
hyperformula_1.HyperFormula.registerFunctionPlugin(customFunctions_1.FinleyCustomFunctions, customFunctions_1.FinleyCustomFunctionsPluginTranslations);
const initialize = (formulas, f1EngineMode) => {
    const f1Engine = adapter_1.f1EngineAdapters.hyperEngine(formulas);
    if (f1EngineMode === 'use') {
        return f1Engine;
    }
    // create hyper formula instance,
    const hfInstance = hyperformula_1.HyperFormula.buildFromArray(
    // Set up row 0 with all the formulas that we need to evaluate later
    // we populate formulas in the cells so that their AST gets cached.
    [formulas.map((formula) => `=${formula}`)], config_1.baseConfig);
    // maintain a map of formula to SimpleCellAddress.
    //  so that we can quickly look up for cell when asked to evaluate given formula
    //  we have setup formulas in 0the row above.
    const formulaToCellMap = new Map(formulas.map((formula, col) => [formula, { sheet: 0, col, row: 0 }]));
    // define TRUE and FALSE constants
    hfInstance.addNamedExpression('TRUE', '=TRUE()');
    hfInstance.addNamedExpression('FALSE', '=FALSE()');
    const evaluate = (formula) => {
        const cellAddress = formulaToCellMap.get(formula);
        if (!cellAddress) {
            throw new Error('unknown formula');
        }
        const result = hfInstance.getCellValue(cellAddress);
        if (result instanceof hyperformula_1.DetailedCellError) {
            (0, common_1.debugLog)(`Error evaluating ${formula}:`, result);
            // add more context to the results.
            const { variables } = (0, utils_1.inspectDependencies)(formula);
            const variablesAndValues = Array.from(variables)
                .map((variable) => {
                const namedExpression = hfInstance.getNamedExpression(variable);
                return `${variable}:[${JSON.stringify(namedExpression)}]`;
            })
                .join('\n');
            throw new Error(`${result.type}:${result.message}, formula: ${formula}\nVariables:\n${variablesAndValues}`);
        }
        if (result === null) {
            (0, common_1.debugLog)(`Unexpected null result for ${formula}:`);
            throw new Error('null result');
        }
        // our formulas sometimes need to return special js specific values
        // like, null or undefined. Since excel formulas do not have such constructs
        // special case some values that our formula will return to represent JS
        // values like `null`, `undefined`
        const mapSpecialValues = new Map([
            ['jsNull', null],
            ['jsUndefined', undefined],
        ]);
        const hyperEngineResult = mapSpecialValues.has(result)
            ? mapSpecialValues.get(result)
            : result;
        if (f1EngineMode === 'compareOnly') {
            f1Engine.compare(formula, hyperEngineResult);
        }
        return hyperEngineResult;
    };
    return {
        setVariables: (variables, options) => {
            if (f1EngineMode) {
                try {
                    f1Engine.setVariables(variables, options);
                }
                catch {
                    // no-op
                }
            }
            // Set new defaults
            const forceStrings = options?.fixupStringVariables ?? new Set();
            let inputs = variables;
            if (options?.flatten) {
                inputs = common_1.utils.generateFlattenNestedKeyObject(variables, {
                    allowAmbiguousKey: Boolean(options.ambiguousKey),
                });
            }
            hfInstance.suspendEvaluation();
            Object.keys(inputs).forEach((key) => {
                // only set variables that are valid formula variables.
                // we already validate that variables referenced are valid.
                if ((0, exports.isValidHyperFormulaVariable)(key)) {
                    // when setting values from string variables. we prefix them with  "`"
                    // that way they will be treated as strings by formulas, even if they can be parsed
                    // as numbers.
                    const val = forceStrings.has(key) &&
                        typeof inputs[key] === 'string' &&
                        inputs[key].length > 0 &&
                        !inputs[key].startsWith("'")
                        ? `'${inputs[key]}`
                        : inputs[key];
                    if (hfInstance.getNamedExpression(key)) {
                        hfInstance.changeNamedExpression(key, val);
                    }
                    else {
                        hfInstance.addNamedExpression(key, val);
                    }
                }
            });
            hfInstance.resumeEvaluation();
        },
        evaluate: (formula) => {
            return evaluate(formula);
        },
        evaluateBoolean: (formula) => {
            const res = evaluate(formula);
            if (typeof res === 'boolean')
                return res;
            throw Error(`expected boolean result but got [${res}] from formula ${formula}`);
        },
        evaluateOptionalBoolean: (formula) => {
            const res = evaluate(formula);
            if (res === undefined || res === null)
                return res;
            if (typeof res === 'boolean')
                return res;
            throw Error(`expected optional boolean result but got [${res}] from formula ${formula}`);
        },
        evaluateNumber: (formula) => {
            const res = evaluate(formula);
            if (typeof res === 'number')
                return res;
            throw Error(`expected number result but got [${res}] from formula ${formula}`);
        },
        evaluateFullDateStr: (formula) => {
            const res = evaluate(formula);
            if (common_1.typeguards.isFullDateStr(res))
                return res;
            throw Error(`expected FullDateStr result but got [${res}] from formula ${formula}`);
        },
        evaluateOptionalNumber: (formula) => {
            const res = evaluate(formula);
            if (res === undefined || res === null)
                return res;
            if (typeof res === 'number')
                return res;
            throw Error(`expected optional number result but got [${res}] from formula ${formula}`);
        },
    };
};
exports.initialize = initialize;
//# sourceMappingURL=hyperEngine.js.map